import { InfoCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import {
  Button,
  Card,
  Checkbox,
  ConfigProvider,
  message,
  Space,
  Table,
  TableColumnsType,
  Tooltip,
} from 'antd';
import Empty from 'components/Empty';
import { useRouteAccessProps } from 'hooks/business/useNewPermisson';
import dayjs from 'lib/dayjs';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import {
  AssitantRealtimeStatus,
  getAssistantRealtimeStats,
  OverseaimConversationAssistantRealtimeStats,
} from 'services/api/dashboard';
import { useFilterCtx } from './Filter';
import { getLocalTimeZone, isToday, useTimeZoneLabel, utcRender } from 'utils/timeZone';
import { useMemoizedFn, useSize } from 'ahooks';
import { OnlineStatusCell } from './OnlineStatusCell';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

const Container = styled(Card)`
  height: 100% !important;
  .ant-card-body {
    padding-top: 16px;
    height: 100% !important;
    .ant-row {
      margin-bottom: 16px;
    }
    .ant-table {
      /* min-height: 600px; */
    }
  }
`;
const ColTitle = styled.div`
  svg {
    margin-left: 4px;
  }
`;

const NickWithTimeZone = styled.div`
  display: flex;
  align-items: center;
  > .anticon {
    margin-left: 6px;
    color: #f2a200;
    cursor: pointer;
  }
`;

const StatusFilter = styled.div`
  .ant-checkbox-group {
    > label {
      display: flex;
      margin-left: 0;
      padding: 5px 12px;
    }
    & + div {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 8px;
      border-top: 1px solid #f0f0f0;
    }
  }
`;

const PerfTableRealTime = (props: {
  adminId?: string;
  departmentSelect?: JSX.Element;
  customSelect?: JSX.Element;
  customerOptions?: { value: string }[];
}) => {
  useEffect(() => {
    posthog.capture('$perfTableRealTime', {
      type: 'entryPage',
    });
  }, []);

  const { adminId, customSelect, departmentSelect, customerOptions } = props;

  const { timeZoneRender, t } = useTimeZoneLabel();

  const localTimeZone = getLocalTimeZone();

  const [params, setParams] = useState<{ assistantIds?: string; pageSize: number }>({
    assistantIds: '',
    pageSize: 10,
  });
  const { newPermissionEnable } = useRouteAccessProps();

  const { whiteListConfig: { ASSIGN_REVIEW_AND_CANCEL_TRADE } = {} } = useOrgConfigStateValue();

  const [value, setValue] = useState<OverseaimConversationAssistantRealtimeStats[]>([]);

  const [updateTime, setUpdateTime] = useState(0);
  const [refresh, setRefresh] = useState(0);

  const { value: filterCtx } = useFilterCtx();
  const { department } = filterCtx;

  const rDepartment = useRef<Dashboard.Department>();

  const rMinAssistantid = useRef<string>();

  // 切换客服
  useEffect(() => {
    rMinAssistantid.current = undefined;
    setValue([]);
    let ids = filterCtx.assistantIds;
    ids = ids && ids.length > 0 ? ids : [];

    if (rDepartment.current?.id === department?.id && ids?.length === 0) {
      ids = customerOptions?.map((v) => v.value);
    }

    const idsStr = ids?.join(',');
    setParams({
      assistantIds: idsStr !== '' ? idsStr : undefined,
      pageSize: 10,
    });
  }, [filterCtx.assistantIds, department, customerOptions]);

  // 切换部门
  useEffect(() => {
    rMinAssistantid.current = undefined;
    setValue([]);
    setParams({
      assistantIds: customerOptions?.map((v) => v.value).join(',') || undefined,
      pageSize: 10,
    });
  }, [customerOptions]);

  useEffect(() => {
    rDepartment.current = department;
  }, [department]);

  useEffect(() => {
    if (!params.assistantIds) {
      setValue([]);
      return () => {};
    }

    let timerId: NodeJS.Timeout;
    getAssistantRealtimeStats({ ...params, minAssistantId: rMinAssistantid.current })
      .then((res) => {
        batchedUpdates(() => {
          if (parseInt(res.nextMinAssistantId, 10) > 0) {
            timerId = setTimeout(() => {
              if (rMinAssistantid.current !== res.nextMinAssistantId) {
                rMinAssistantid.current = res.nextMinAssistantId;
                setRefresh((v) => v + 1);
              }
            }, 1000);
          }

          setUpdateTime(Date.now());

          setValue((v) => {
            const ids: string[] = [];

            v.forEach((r) => {
              ids.push(r.id);
            });

            const stats = res.stats.filter((r) => !ids.includes(r.id));

            return [...v, ...stats];
          });
        });
      })
      .catch(() => {
        message.error(t('translation.操作失败，请稍后再试'));
      });

    return () => {
      if (timerId) {
        clearTimeout(timerId);
      }
    };
  }, [params, refresh, newPermissionEnable, t]);

  const dataSource = useMemo(() => {
    const list = value?.filter((x) => String(x.id) !== adminId);
    const online: OverseaimConversationAssistantRealtimeStats[] = [];
    const hangup: OverseaimConversationAssistantRealtimeStats[] = [];
    const offline: OverseaimConversationAssistantRealtimeStats[] = [];
    list.forEach((item) => {
      const { status } = item;
      if (status === 'ONLINE') {
        online.push(item);
      }
      if (status === 'HANGUP') {
        hangup.push(item);
      }
      if (status === 'OFFLINE') {
        offline.push(item);
      }
    });

    return [...online, ...hangup, ...offline];
  }, [value, adminId]);

  const totalSecondsRender = useMemoizedFn((value: number) => {
    return !value
      ? '-'
      : dayjs
          .duration(value * 1000)
          .format('HH:mm:ss')
          .padStart(8, '00:');
  });

  const timestampRender = useMemoizedFn(
    (value: string, { timeZone }: OverseaimConversationAssistantRealtimeStats) =>
      parseInt(value, 10) === 0
        ? '-'
        : `${dayjs(parseInt(value, 10)).utcOffset(timeZone).format('HH:mm:ss')}${
            timeZone === localTimeZone ? '' : `(${utcRender(timeZone)})`
          }`,
  );

  // 子状态列
  const subStatusCols = useMemo(() => {
    const cols: TableColumnsType<OverseaimConversationAssistantRealtimeStats> = [];
    dataSource.forEach((data) => {
      data.subStatusDuration?.forEach((each) => {
        const { status, subStatus } = each;
        if (status === 'HANGUP' && subStatus && !cols.find((col) => col.title === subStatus)) {
          cols.push({
            title: subStatus,
            width: 120,
            render: (_, record) => {
              const value = record.subStatusDuration?.find(
                (x) => x.subStatus === subStatus,
              )?.seconds;
              return value ? totalSecondsRender(value) : null;
            },
          });
        }
      });
    });
    return cols;
  }, [dataSource, totalSecondsRender]);

  const columns: TableColumnsType<OverseaimConversationAssistantRealtimeStats> = useMemo(
    () => [
      {
        className: 'head-col assistantId',
        title: 'ID',
        fixed: 'left',
        dataIndex: 'id',
        width: 40,
      },
      {
        className: 'head-col assistantName',
        title: t('ViewDashboard.帐号昵称'),
        fixed: 'left',
        dataIndex: 'name',
        width: 120,
        render: (_, { name, timeZone, timeZoneUpdateTime }) =>
          Number(timeZoneUpdateTime) && isToday(Number(timeZoneUpdateTime), timeZone) ? (
            <NickWithTimeZone>
              {name}
              <Tooltip
                title={
                  <>
                    <div>
                      {t('account_work_timeZone_update_tips', {
                        x: timeZoneRender(timeZone, false),
                      })}
                    </div>
                    <div>{t('account_work_timeZone_update_tips2')}</div>
                  </>
                }
              >
                <InfoCircleFilled />
              </Tooltip>
            </NickWithTimeZone>
          ) : (
            name
          ),
      },

      // #region  状态
      {
        className: 'head-col assistantName',
        title: t('ViewDashboard.状态'),
        dataIndex: 'status',
        fixed: 'left',
        width: 210,
        render: (text: AssitantRealtimeStatus, record) => {
          return <OnlineStatusCell status={text} userId={record.id} />;
        },
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
          return (
            <StatusFilter>
              <Checkbox.Group
                value={selectedKeys}
                onChange={(values) => setSelectedKeys(values as any)}
              >
                <Checkbox value="ONLINE">{t('status.online')}</Checkbox>
                <Checkbox value="HANGUP">{t('status.hangup')}</Checkbox>
                <Checkbox value="OFFLINE">{t('status.offline')}</Checkbox>
              </Checkbox.Group>
              <div>
                <Button type="text" size="small" onClick={clearFilters}>
                  {t('filter.reset')}
                </Button>
                <Button type="primary" size="small" onClick={() => confirm()}>
                  {t('phrase.确定')}
                </Button>
              </div>
            </StatusFilter>
          );
        },
        onFilter: (value, record) => record.status === value,
      },
      // #endregion 状态

      {
        className: 'head-col assistantName',
        title: t('ViewDashboard.累计在线时长'),
        dataIndex: 'totalOnlineSeconds',
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => a.totalOnlineSeconds - b.totalOnlineSeconds,
        render: totalSecondsRender,
      },

      {
        className: 'head-col assistantName',
        title: t('perf_report_real_time_title_total_hangup'),
        dataIndex: 'totalHangSeconds',
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => a.totalHangSeconds - b.totalHangSeconds,
        render: totalSecondsRender,
      },

      {
        className: 'head-col assistantName',
        title: t('perf_report_real_time_title_total_hangup_times'),
        dataIndex: 'totalHangTimes',
        width: 90,
        showSorterTooltip: false,
      },

      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.待处理买家数')}
            <Tooltip title={t('ViewDashboard.待处理买家数TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'unprocessedWhenOffline',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.unprocessed - b.unprocessed,
      },
      ...(ASSIGN_REVIEW_AND_CANCEL_TRADE
        ? ([
            {
              className: 'head-col assistantName',
              title: t('recepted_cancel_order_count'),
              dataIndex: 'cancelOrderAssigned',
              width: 90,
              showSorterTooltip: false,
              sorter: (a, b) => a.cancelOrderAssigned - b.cancelOrderAssigned,
            },
            {
              className: 'head-col assistantName',
              title: t('recepted_review_count'),
              dataIndex: 'negRateAssigned',
              width: 90,
              showShorterTooltip: false,
              sorter: (a, b) => a.negRateAssigned - b.negRateAssigned,
            },
          ] as TableColumnsType<OverseaimConversationAssistantRealtimeStats>)
        : []),
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.排队中买家数')}
            <Tooltip title={t('ViewDashboard.排队中买家数TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'waitAssign',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.waitAssign - b.waitAssign,
      },

      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.回复买家总数')}
            <Tooltip title={t('ViewDashboard.回复买家总数TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'totalReply',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.totalReply - b.totalReply,
      },

      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.人工回复买家总数')}
            <Tooltip title={t('ViewDashboard.人工回复买家总数TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'totalManualReply',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.totalManualReply - b.totalManualReply,
      },
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.分配买家总数')}
            <Tooltip title={t('ViewDashboard.分配买家总数TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'totalAssign',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.totalAssign - b.totalAssign,
      },
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('已付款买家数')}
            <Tooltip title={t('已付款买家数tips')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'paidCount',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.paidCount - b.paidCount,
      },
      {
        className: 'head-col assistantName',
        title: t('ViewDashboard.强提醒买家数'),
        dataIndex: 'remind',
        width: 90,
        showSorterTooltip: false,
        sorter: (a, b) => a.remind - b.remind,
      },
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('min_last_buyer_msg_time')}
            <Tooltip title={t('min_last_buyer_msg_time_tips')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'minLastBuyerMsgTime',
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => Number(a.minLastBuyerMsgTime) - Number(b.minLastBuyerMsgTime),
        render: (time) => (Number(time) ? dayjs(Number(time)).format('YYYY-MM-DD HH:mm:ss') : '-'),
      },
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.上线时间')}
            <Tooltip title={t('ViewDashboard.上线时间TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'onlineTimestamp',
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => parseInt(a.onlineTimestamp, 10) - parseInt(b.onlineTimestamp, 10),
        render: timestampRender,
      },
      {
        className: 'head-col assistantName',
        dataIndex: 'hangTimestamp',
        title: (
          <ColTitle>
            {t('perf_report_real_time_title_hangup_last_time')}
            <Tooltip title={t('perf_report_real_time_title_hangup_last_time_tip')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => parseInt(a.hangTimestamp, 10) - parseInt(b.hangTimestamp, 10),
        render: timestampRender,
      },
      {
        className: 'head-col assistantName',
        title: (
          <ColTitle>
            {t('ViewDashboard.离线时间')}
            <Tooltip title={t('ViewDashboard.离线时间TIP')}>
              <QuestionCircleOutlined />
            </Tooltip>
          </ColTitle>
        ),
        dataIndex: 'offlineTimestamp',
        width: 120,
        showSorterTooltip: false,
        sorter: (a, b) => parseInt(a.offlineTimestamp, 10) - parseInt(b.offlineTimestamp, 10),
        render: timestampRender,
      },
      {
        className: 'head-col assistantName',
        key: 'date',
        title: t('marketing.日期'),
        width: 120,
        render: (_, { timeZone }) => dayjs().utcOffset(timeZone).format('YYYY-MM-DD'),
      },
      ...subStatusCols,
    ],
    [
      t,
      timeZoneRender,
      totalSecondsRender,
      timestampRender,
      subStatusCols,
      ASSIGN_REVIEW_AND_CANCEL_TRADE,
    ],
  );

  const extraNode = useMemo(() => {
    return (
      <div
        className={css`
          display: flex;
          justify-content: space-between;
          margin-bottom: 12px;
        `}
      >
        <Space size={16}>
          {departmentSelect}
          {newPermissionEnable && department === undefined ? null : customSelect}
        </Space>
        <Space size={16}>
          {updateTime > 0 ? (
            <span>
              {t('ViewDashboard.更新时间')}:{dayjs(updateTime).format('YYYY-MM-DD HH:mm:ss')}(
              {utcRender()})
            </span>
          ) : null}
          <Button
            onClick={() => {
              batchedUpdates(() => {
                rMinAssistantid.current = undefined;
                setValue([]);
                setRefresh((v) => v + 1);
              });
            }}
          >
            {t('ViewDashboard.刷新实时数据')}
          </Button>
        </Space>
      </div>
    );
  }, [customSelect, departmentSelect, updateTime, department, newPermissionEnable, t]);
  const ref = useRef<HTMLDivElement>(null);
  const height = useSize(ref)?.height;
  const [headHeight, setHeadHeight] = useState(0);

  useLayoutEffect(() => {
    const head = ref.current?.querySelector('.ant-table-cell.head-col');
    setTimeout(() => {
      setHeadHeight(head?.clientHeight || 0);
    });
  }, [dataSource]);

  return (
    <Container>
      <div className="h-full" ref={ref}>
        {extraNode}

        {newPermissionEnable && department === undefined ? null : (
          <ConfigProvider renderEmpty={() => <Empty />} autoInsertSpaceInButton={false}>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={dataSource}
              scroll={{ x: 'max-content', y: height ? height - headHeight - 47 : 0 }}
              pagination={false}
            />
          </ConfigProvider>
        )}
      </div>
    </Container>
  );
};

export default PerfTableRealTime;
